
import React, { useEffect, useRef } from 'react';
import { Plane, Ship, Truck, Globe, Send, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { smoothScrollTo } from '@/utils/smoothScroll';
import { MESSENGERS } from '@/components/MessengerLinks';
import { trackContactClick } from '@/lib/analytics';

/** Легкий паралакс: шар зсувається повільніше за скрол */
const useParallax = (factor: number) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.style.transform = `translate3d(0, ${window.scrollY * factor}px, 0)`;
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [factor]);

  return ref;
};

const Hero = () => {
  const { t } = useLanguage();
  const telegram = MESSENGERS.find((m) => m.channel === 'telegram')!;
  const patternRef = useParallax(0.2);
  const iconsRef = useParallax(0.1);

  const directions = [
    { icon: Plane, label: t('hero.dir.china') },
    { icon: Ship, label: t('hero.dir.usa') },
    { icon: Truck, label: t('hero.dir.europe') },
  ];

  return (
    <section id="home" className="relative min-h-screen bg-gradient-to-br from-navy via-primary to-teal overflow-hidden">
      {/* Background Pattern (parallax layer) */}
      <div ref={patternRef} className="absolute inset-0 opacity-10" aria-hidden="true">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full border-2 border-white"></div>
        <div className="absolute top-40 right-20 w-20 h-20 rounded-full border-2 border-orange"></div>
        <div className="absolute bottom-40 left-1/4 w-16 h-16 rounded-full border-2 border-white"></div>
      </div>

      <div className="container mx-auto px-4 py-12 md:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[80vh]">
          {/* Left Content */}
          <div className="text-white space-y-6 md:space-y-8 text-center lg:text-left">
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                <span className="block">{t('hero.title1')}</span>
                <span className="block text-orange">{t('hero.title2')}</span>
                <span className="block">{t('hero.title3')}</span>
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl opacity-90 max-w-2xl mx-auto lg:mx-0">
                {t('hero.subtitle')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                size="lg"
                className="group bg-orange hover:bg-orange/90 text-navy font-semibold px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                onClick={() => {
                  smoothScrollTo('contact');
                }}
              >
                {t('hero.getQuote')}
                <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-navy px-6 md:px-8 py-3 md:py-4 rounded-full text-base md:text-lg transition-all duration-300"
                asChild
              >
                <a
                  href={telegram.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContactClick('telegram')}
                >
                  <Send className="w-5 h-5 mr-2" />
                  {t('hero.telegram')}
                </a>
              </Button>
            </div>

            {/* Delivery Directions */}
            <div className="pt-6 md:pt-8">
              <div className="text-xs uppercase tracking-wider opacity-70 mb-3">{t('hero.dir.label')}</div>
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                {directions.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 hover:bg-white/20 transition-colors duration-300"
                  >
                    <Icon className="w-4 h-4 text-orange" />
                    <span className="text-sm font-medium whitespace-nowrap">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content - Floating Icons (parallax layer) */}
          <div ref={iconsRef} className="relative hidden lg:block" aria-hidden="true">
            <div className="relative w-full h-96">
              <div className="absolute top-20 left-20 animate-float">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-6 hover:bg-white/30 transition-all duration-300">
                  <Plane className="w-12 h-12 text-orange" />
                </div>
              </div>

              <div className="absolute bottom-20 right-20 animate-float-slow" style={{ animationDelay: '1.2s' }}>
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-6 hover:bg-white/30 transition-all duration-300">
                  <Ship className="w-12 h-12 text-orange" />
                </div>
              </div>

              <div className="absolute top-40 right-10 animate-float" style={{ animationDelay: '0.6s' }}>
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 hover:bg-white/30 transition-all duration-300">
                  <Truck className="w-8 h-8 text-white" />
                </div>
              </div>

              <div className="absolute bottom-40 left-10 animate-float-slow" style={{ animationDelay: '2s' }}>
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 hover:bg-white/30 transition-all duration-300">
                  <Globe className="w-8 h-8 text-white" />
                </div>
              </div>

              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 300">
                <path
                  d="M100 100 Q200 150 300 200"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                />
                <path
                  d="M300 80 Q200 120 100 220"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Wave Bottom */}
      <div className="absolute bottom-0 w-full" aria-hidden="true">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12 md:h-16 fill-white">
          <path d="M0,120 L0,80 Q300,0 600,80 T1200,80 L1200,120 Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
